import type {CSSProperties} from 'react';
import {font, radius, weight} from '../scale.js';
import {wedgeExactRole} from './wedgeRoles.js';

export type WedgeDealLook = 'deal-seg' | 'deal-seg-on' | 'deal-back' | 'deal-pitch-copy' | 'deal-copy' | 'deal-export' | 'deal-save' | 'deal-saved-row';

/* Released segmented control of the Deal builder (DealSeg): one native button per option, in a grid of equal columns that the caller
   draws. The button is a 10px radius box with 9px by 4px padding and 10.5px heavy mono type, 0.6px tracking, uppercase. Idle it takes the
   surface fill, the secondary ink and the bold separator border; chosen it takes the accent fill, the accent border and the ink for
   accent fills. Each color reads the exact legacy role, so the shipped ink rules and the host theme variables keep matching. The caller
   owns the grid, the label, the delight class and the handler. The visible box is shorter than 44px and its row neighbors sit 12px away
   above and below and 6px aside, so the 44px settings hit region (grown above and below, never aside) overlaps no neighbor and stays on. */
const PADDING_Y = 9, PADDING_X = 4, FONT_SIZE = 10.5, TRACKING = 0.6;

/* Released back button of the Deal builder header (DealBuilderScreen): a 34px square with a 10px radius on the surface fill, the label ink
   and the bold separator border, one 17px bold glyph that the caller draws. It declares no padding and no family, as shipped. Each color
   reads the exact legacy role. The caller owns the label, the delight class and the click. Its only neighbor is the title block 10px to
   the right, and 14px, 16px and 10px of free header sit on the other sides, so the 44px settings hit region (5px more on every side)
   overlaps no neighbor and stays on. */
const BACK_BOX = 34;

/* Released result actions of the Deal builder (DealBuilderScreen): Copy pitch under the pitch line, and Copy one-pager, the card export and
   Save in the One-pager card. Each is a 10px radius box in heavy mono type, uppercase, with a 0.8px track and a pointer cursor. Copy pitch
   is 11px type with 8px by 14px padding on the soft accent fill, with the accent border and the dark accent ink. The three one-pager
   actions are 11.5px type with 10px by 16px padding: Copy takes the accent fill, no border and the ink for accent fills; the export takes
   the surface fill, the accent border and the dark accent ink; Save takes the surface fill, the bold separator border and the label ink.
   The caller owns the label, the delight and copy-flash classes, the click and the placement (Copy pitch sits at the start of its grid).
   Each color reads the exact legacy role, so the shipped ink rules and the host theme variables keep matching. */
const ACTION_TRACKING = 0.8;
const ACTIONS = {
  'deal-pitch-copy': {padding: '8px 14px', size: font.micro, border: 'accent', fill: 'accentTint', ink: 'accentDark'},
  'deal-copy': {padding: '10px 16px', size: 11.5, border: null, fill: 'accent', ink: 'onAccent'},
  'deal-export': {padding: '10px 16px', size: 11.5, border: 'accent', fill: 'surface', ink: 'accentDark'},
  'deal-save': {padding: '10px 16px', size: 11.5, border: 'separatorBold', fill: 'surface', ink: 'label'},
} as const;

/* Released saved deal row of the Deal builder (DealBuilderScreen): a full-width flex row with the name block at the start and the
   OPEN or LOAD word at the end, 8px apart, centered on the cross axis. It is a 10px radius box with 10px by 12px padding, left-aligned
   text, the hairline separator border and the alternate surface fill. The caller owns the two text blocks and the delight class. */
const ROW_PY = 10, ROW_PX = 12, ROW_GAP = 8;

export function wedgeDealStyle(look: WedgeDealLook): CSSProperties {
  if (look === 'deal-back') return {
    width: BACK_BOX, height: BACK_BOX, borderRadius: radius.md, border: `1px solid ${wedgeExactRole('separatorBold')}`,
    background: wedgeExactRole('surface'), color: wedgeExactRole('label'), fontSize: font.title3, fontWeight: weight.bold, cursor: 'pointer',
  };
  if (look === 'deal-saved-row') return {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: ROW_GAP, padding: `${ROW_PY}px ${ROW_PX}px`, borderRadius: radius.md,
    cursor: 'pointer', textAlign: 'left', border: `1px solid ${wedgeExactRole('separator')}`, background: wedgeExactRole('surfaceAlt'),
  };
  if (look in ACTIONS) {
    const a = ACTIONS[look as keyof typeof ACTIONS];
    return {
      padding: a.padding, borderRadius: radius.md, cursor: 'pointer', border: a.border ? `1px solid ${wedgeExactRole(a.border)}` : 'none',
      background: wedgeExactRole(a.fill), color: wedgeExactRole(a.ink),
      fontFamily: wedgeExactRole('mono'), fontSize: a.size, fontWeight: weight.heavy, letterSpacing: ACTION_TRACKING, textTransform: 'uppercase',
    };
  }
  const on = look === 'deal-seg-on';
  return {
    padding: `${PADDING_Y}px ${PADDING_X}px`, borderRadius: radius.md, cursor: 'pointer',
    border: `1px solid ${wedgeExactRole(on ? 'accent' : 'separatorBold')}`,
    background: wedgeExactRole(on ? 'accent' : 'surface'),
    color: wedgeExactRole(on ? 'onAccent' : 'labelSec'),
    fontFamily: wedgeExactRole('mono'), fontSize: FONT_SIZE, fontWeight: weight.heavy, letterSpacing: TRACKING, textTransform: 'uppercase',
  };
}
